import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button, Col, Row, Typography } from 'antd';
import cs from 'classnames';
import dayjs from 'dayjs';

import Alert from 'components/Alert/Alert';
import EditJobButton from 'components/EditJobButton/EditJobButton';
import JobSubscriptionStatus from 'components/JobSubscriptionStatus/JobSubscriptionStatus';
import Tags from 'components/Jobseekers/components/Tags';
import LocationDisplay from 'components/LocationDisplay/LocationDisplay';
import StageSelector from 'components/StageSelector/StageSelector';
import * as jobStatusConfig from 'components/StageSelector/jobStatusConfig';
import StatusIcon from 'components/StatusIcon/StatusIcon';

import { selectClientCardLastFourDigits } from 'features/client/clientSelectors';
import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import formatAndOrderData from 'utils/formatAndOrderData';
import { isNil } from 'utils/isNil';

import { FormattedOption } from 'types/dictionaries';
import { Job } from 'types/jobs';

import {
  Dictionaries,
  JobStatus,
  PaymentStatus,
  SubscriptionTypes
} from 'constants/enums';
import languagesOrder from 'constants/languagesOrder';
import { DETAILS_PANEL_ROW_GUTTER } from 'constants/uiSettings';
import { DATE_FORMAT, FALLBACK_VALUE } from 'constants/uiSettings';

import Salary from '../Salary/Salary';
import styles from './JobInfoView.module.scss';
import { getJobInfoViewConfig } from './config/jobInfoViewConfig';
import { getJobInfoViewState } from './utils/getJobInfoViewState';

interface JobInfoViewProps {
  jobId: string | null;
  job: Job | null;
  isLoading: boolean;
  isPaymentLoading: boolean;
  onReactivate: () => void;
  onRetryPayment: () => void;
  onUpdatePayment: (isRetryPayment: boolean) => () => void;
  onCloseJob: () => void;
  onCloseFreeJob: () => void;
}

const JobInfoView = ({
  jobId,
  job,
  isLoading,
  isPaymentLoading,
  onReactivate,
  onRetryPayment,
  onUpdatePayment,
  onCloseJob,
  onCloseFreeJob
}: JobInfoViewProps) => {
  const { t } = useTranslation();

  const skillsetDict = useSelector(
    selectDictionary(Dictionaries.Skillset)
  ) as FormattedOption[];
  const jobStatuses = useSelector(selectDictionary(Dictionaries.JobStatus));
  const jobTypeDict = useSelector(selectDictionary(Dictionaries.JobType));
  const languagesDict = useSelector(selectDictionary(Dictionaries.Language));
  const clientCardLastFourDigits = useSelector(selectClientCardLastFourDigits);
  const languagesOptions = formatAndOrderData(languagesDict, languagesOrder);

  const isFreeJob = job?.subscriptionType === SubscriptionTypes.Free;
  const isFreeActiveJob = isFreeJob && job?.status === JobStatus.Active;

  const config = getJobInfoViewConfig({
    onReactivate,
    onRetryPayment,
    onUpdatePayment: onUpdatePayment(true),
    onCloseJob
  });

  const {
    alertType,
    alertText,
    actionButton,
    actionButtonColSpan,
    alertButton,
    isAdditionalUpdatePaymentButton
  } = getJobInfoViewState({
    isFreeJob,
    subscriptionStatus: job?.subscriptionStatus,
    config
  });

  const message = alertButton ? (
    <div className={styles.messageWithAlertActionButton}>
      {alertText}
      <Button
        className={styles.alertActionButton}
        key={alertButton.key}
        type="primary"
        onClick={alertButton.onClick}
        loading={isPaymentLoading}
      >
        {alertButton.label}
      </Button>
    </div>
  ) : (
    alertText
  );

  const shouldShowNextRenewal =
    job?.subscriptionStatus === PaymentStatus.Active &&
    !!job?.subscriptionDueDate;

  return (
    <aside className={styles.jobDetailsContainer}>
      {isLoading || !jobId || !job ? (
        <div className={styles.loadingState}>{t('common.loading')}</div>
      ) : (
        <>
          {alertText && (
            <Row className={styles.row}>
              <Col span={24}>
                <Alert type={alertType} message={message} />
              </Col>
            </Row>
          )}

          <Row className={styles.row} gutter={DETAILS_PANEL_ROW_GUTTER}>
            <Col xs={12}>
              <StageSelector
                value={job.status}
                hasPermission
                isDisplayIcons
                id={jobId}
                options={jobStatuses}
                config={jobStatusConfig}
                dictionary={Dictionaries.JobClientStatusFlow}
              />
              <div className={styles.label}>{t('sidebar.job.status')}</div>
            </Col>

            <Col xs={12}>
              <div>
                <JobSubscriptionStatus job={job} />
              </div>
              <div className={styles.label}>
                {t('card.job.subscriptionStatus')}
              </div>
            </Col>

            <Col xs={12}>
              {isNil(job?.interviewSent) ? FALLBACK_VALUE : job.interviewSent}
              <div className={styles.label}>
                {t('sidebar.job.numberOfInterviews')}
              </div>
            </Col>
            <Col xs={12}>
              {isNil(job?.offerSent) ? FALLBACK_VALUE : job.offerSent}
              <div className={styles.label}>
                {t('sidebar.job.numberOfOffersSent')}
              </div>
            </Col>
          </Row>

          <Row className={styles.row}>
            <div className={styles.subheader}>{t('common.jobDetails')}</div>
            <div className={styles.divider} />
          </Row>

          <Row className={styles.row} gutter={DETAILS_PANEL_ROW_GUTTER}>
            <Col xl={6} sm={12} xs={24}>
              <LocationDisplay
                location={job?.location}
                countryData={job?.country}
                address={job?.address}
              />
              <div className={styles.label}>{t('sidebar.job.jobAddress')}</div>
            </Col>
            <Col xl={6} sm={12} xs={24}>
              <div className={styles.cut}>
                {job?.workSchedule || FALLBACK_VALUE}
              </div>
              <div className={styles.label}>
                {t('sidebar.job.workSchedule')}
              </div>
            </Col>
            <Col xl={6} sm={12} xs={24}>
              <Salary job={job} />
              <div className={styles.label}>{t('sidebar.job.salary')}</div>
            </Col>
            <Col xl={6} sm={12} xs={24}>
              <Tags
                key={job.jobType?.join('')}
                dictionary={jobTypeDict}
                values={job.jobType}
                maxVisible={6}
              />
              <div className={styles.label}>
                {t('sidebar.job.contractType')}
              </div>
            </Col>
          </Row>

          <Row className={styles.row} gutter={DETAILS_PANEL_ROW_GUTTER}>
            <Col span={8} className={styles.status}>
              <StatusIcon status={job?.benefits} />
              <div className={styles.label}>{t('sidebar.job.benefits')}</div>
            </Col>

            <Col span={8} className={styles.status}>
              <StatusIcon status={job?.backCheck} />
              <div className={styles.label}>
                {t('sidebar.job.backgroundCheckConsent')}
              </div>
            </Col>

            <Col span={8} className={styles.status}>
              <StatusIcon status={job?.drugTest} />
              <div className={styles.label}>{t('sidebar.job.drugTest')}</div>
            </Col>
          </Row>

          <Row className={styles.row} gutter={DETAILS_PANEL_ROW_GUTTER}>
            <Col sm={12} xs={24}>
              <Tags dictionary={languagesOptions} values={job?.language} />
              <div className={styles.label}>
                {t('sidebar.job.languageRequirement')}
              </div>
            </Col>
            <Col sm={12} xs={24}>
              <Tags
                dictionary={skillsetDict}
                values={job?.skillRequirement}
                maxVisible={6}
              />
              <div className={styles.label}>
                {t('sidebar.job.skillsetsRequired')}
              </div>
            </Col>
          </Row>

          {job?.description && (
            <>
              <Row className={styles.row}>
                <div className={styles.subheader}>
                  {t('sidebar.job.jobDescription')}
                </div>
                <div className={styles.divider} />
              </Row>

              <Row className={styles.row}>
                <Col span={24}>
                  <Typography.Paragraph className={styles.jobDescription}>
                    {job.description}
                  </Typography.Paragraph>
                </Col>
              </Row>
            </>
          )}

          {job?.qualifications && (
            <>
              <Row className={styles.row}>
                <div className={styles.subheader}>
                  {t('sidebar.job.jobQualifications')}
                </div>
                <div className={styles.divider} />
              </Row>

              <Row className={styles.row}>
                <Col span={24}>
                  <Typography.Paragraph className={styles.jobDescription}>
                    {job.qualifications}
                  </Typography.Paragraph>
                </Col>
              </Row>
            </>
          )}

          {job?.workEnvironmentAndPhysicalRequirements && (
            <>
              <Row className={styles.row}>
                <div className={styles.subheader}>
                  {t('sidebar.job.workEnvironmentAndPhysicalRequirements')}
                </div>
                <div className={styles.divider} />
              </Row>

              <Row className={styles.row}>
                <Col span={24}>
                  <Typography.Paragraph className={styles.jobDescription}>
                    {job.workEnvironmentAndPhysicalRequirements}
                  </Typography.Paragraph>
                </Col>
              </Row>
            </>
          )}

          {!isFreeJob && (
            <>
              <Row className={styles.row}>
                <div className={styles.subheader}>
                  {t('sidebar.job.subscriptionSummary')}
                </div>
                <div className={styles.divider} />
              </Row>

              <Row className={styles.row} gutter={DETAILS_PANEL_ROW_GUTTER}>
                <Col xl={6} sm={12} xs={24}>
                  {/* createdDate used as  billing start date*/}
                  <div>
                    {job?.createdDate
                      ? dayjs(job.createdDate).format(DATE_FORMAT)
                      : FALLBACK_VALUE}
                  </div>
                  <div className={styles.label}>
                    {t('sidebar.job.billingStartDate')}
                  </div>
                </Col>
                {shouldShowNextRenewal && (
                  <Col xl={6} sm={12} xs={24}>
                    <div>
                      {dayjs(job.subscriptionDueDate).format(DATE_FORMAT)}
                    </div>
                    <div className={styles.label}>
                      {t('sidebar.job.nextRenewal')}
                    </div>
                  </Col>
                )}
                <Col xl={6} sm={12} xs={24}>
                  {
                    <div>
                      {clientCardLastFourDigits
                        ? `**** **** **** ${clientCardLastFourDigits}`
                        : FALLBACK_VALUE}
                    </div>
                  }
                  <div className={styles.label}>
                    {t('sidebar.job.paymentMethod')}
                  </div>
                </Col>
                <Col xl={6} sm={12} xs={24}>
                  <div>
                    <JobSubscriptionStatus job={job} />
                  </div>
                  <div className={styles.label}>
                    {t('card.job.subscriptionStatus')}
                  </div>
                </Col>
                {actionButton && (
                  <Col
                    xl={actionButtonColSpan.xl}
                    sm={actionButtonColSpan.sm}
                    xs={actionButtonColSpan.xs}
                  >
                    <Row
                      className={cs(styles.row, {
                        [styles.rowNoMarginBottom]:
                          job.subscriptionStatus === PaymentStatus.Active
                      })}
                    >
                      <Button
                        key={actionButton.key}
                        type="primary"
                        className={styles.button}
                        onClick={actionButton.onClick}
                        loading={isPaymentLoading}
                      >
                        {actionButton.label}
                      </Button>
                    </Row>
                  </Col>
                )}
              </Row>
            </>
          )}

          {isFreeActiveJob && (
            <Row className={styles.row}>
              <Col span={24}>
                <Button
                  type="primary"
                  className={styles.button}
                  onClick={onCloseFreeJob}
                  loading={isPaymentLoading}
                >
                  {t('sidebar.job.closeJob')}
                </Button>
              </Col>
            </Row>
          )}

          {isAdditionalUpdatePaymentButton && (
            <Row className={styles.row}>
              <Col span={24}>
                <Button
                  type="primary"
                  className={styles.button}
                  onClick={onUpdatePayment(false)}
                  disabled={isPaymentLoading}
                >
                  {t('sidebar.job.updatePayment')}
                </Button>
              </Col>
            </Row>
          )}

          <Row className={cs(styles.row, styles.editButtonContainer)}>
            <EditJobButton job={job} className={styles.editButton} />
          </Row>
        </>
      )}
    </aside>
  );
};

export default JobInfoView;
